import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarDays,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  ClipboardList,
  Coins,
  MessageCircle,
  Star,
  Wallet,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { formatNumber } from '@/lib/format';
import { useUserId } from '@/components/AuthProvider';
import Spinner from '@/components/Spinner';
import styles from './Dashboard.module.css';

type Stats = {
  totalPrestations: number;
  monthlyPrestations: number;
  totalProfit: number;
  monthlyProfit: number;
  rating: number;
  reviewsCount: number;
  acceptanceRate: number;
};

const EMPTY_STATS: Stats = {
  totalPrestations: 0,
  monthlyPrestations: 0,
  totalProfit: 0,
  monthlyProfit: 0,
  rating: 0,
  reviewsCount: 0,
  acceptanceRate: 0,
};

export default function ProviderDashboard() {
  const userId = useUserId();
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('');
  const [applicationStatus, setApplicationStatus] = useState<string | null>(null);
  const [stats, setStats] = useState<Stats>(EMPTY_STATS);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', userId)
        .maybeSingle();
      if (profile?.full_name) setUserName(profile.full_name);

      const { data: application } = await supabase
        .from('provider_applications')
        .select('status')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      setApplicationStatus(application?.status ?? null);

      const [
        { data: bookingsData },
        { data: acceptedProposals },
        { data: providerData },
        { data: reviewsData },
      ] = await Promise.all([
        supabase
          .from('bookings')
          .select('id, status, final_price, preferred_date, created_at')
          .eq('provider_id', userId),
        supabase
          .from('booking_proposals')
          .select('booking_id, price')
          .eq('provider_id', userId)
          .eq('status', 'accepted'),
        supabase.from('providers').select('rating').eq('id', userId).maybeSingle(),
        supabase
          .from('bookings')
          .select('id')
          .eq('provider_id', userId)
          .eq('status', 'completed')
          .not('rating', 'is', null),
      ]);

      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();

      const acceptedPriceByBooking = new Map<string, number>(
        (acceptedProposals ?? []).map((proposal: any) => [
          proposal.booking_id,
          Number(proposal.price ?? 0),
        ])
      );

      const bookings = bookingsData ?? [];
      const withPrice = bookings.map((booking: any) => ({
        ...booking,
        acceptedPrice: Number(
          booking.final_price ?? acceptedPriceByBooking.get(booking.id) ?? 0
        ),
      }));

      const completed = bookings.filter((booking: any) => booking.status === 'completed');
      const revenue = withPrice.filter(
        (booking: any) =>
          booking.acceptedPrice > 0 && !['cancelled', 'rejected'].includes(booking.status)
      );
      const decided = bookings.filter((booking: any) =>
        ['completed', 'cancelled', 'rejected'].includes(booking.status)
      );

      const inCurrentMonth = (booking: any) => {
        const date = new Date(booking.preferred_date ?? booking.created_at);
        return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
      };

      const sum = (items: any[]) =>
        items.reduce((total, booking) => total + Number(booking.acceptedPrice ?? 0), 0);

      setStats({
        totalPrestations: completed.length,
        monthlyPrestations: completed.filter(inCurrentMonth).length,
        totalProfit: sum(revenue),
        monthlyProfit: sum(revenue.filter(inCurrentMonth)),
        rating: Number(providerData?.rating ?? 0),
        reviewsCount: reviewsData?.length ?? 0,
        acceptanceRate:
          decided.length === 0 ? 0 : Math.round((completed.length / decided.length) * 100),
      });
    } catch (err) {
      console.error('Error loading provider dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <Spinner block label="Chargement de vos performances..." />;

  if (applicationStatus === 'pending') {
    return (
      <div className={styles.statusScreen}>
        <img className={styles.statusArt} src="/assets/illustrations/hourglass.png" alt="" />
        <h1 className={styles.statusTitle}>Demande en cours d&apos;examen</h1>
        <p className={styles.statusText}>
          Votre demande a bien été reçue. L&apos;administration vérifiera vos informations et vous
          notifiera une fois votre compte approuvé.
        </p>
      </div>
    );
  }

  if (applicationStatus === 'rejected') {
    return (
      <div className={styles.statusScreen}>
        <span className={styles.statusIcon}>
          <CircleAlert size={40} />
        </span>
        <h1 className={`${styles.statusTitle} ${styles.statusTitleError}`}>Demande rejetée</h1>
        <p className={styles.statusText}>
          Votre demande a été rejetée. Veuillez contacter l&apos;administration pour plus
          d&apos;informations.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <h1 className={styles.greeting}>Bonjour, {userName || 'Prestataire'}</h1>
          <p className={styles.subtitle}>Voici vos performances</p>
          <Link className={`btn btn--on-brand ${styles.heroAction}`} to="/pro/requests?tab=available">
            Voir les demandes disponibles <ChevronRight size={18} />
          </Link>
        </div>
      </section>

      <div className={styles.body}>
        <div className={styles.revenueCard}>
          <div>
            <p className={styles.revenueLabel}>Revenus du mois</p>
            <p className={`${styles.revenueAmount} num`}>
              {formatNumber(stats.monthlyProfit)} <span className={styles.currency}>MAD</span>
            </p>
          </div>
          <img className={styles.revenueImage} src="/assets/illustrations/money.png" alt="" />
        </div>

        <section>
          <h2 className={styles.sectionTitle}>Mes statistiques</h2>
          <div className={styles.statsGrid}>
            <StatCard
              icon={<ClipboardList size={26} color="var(--accent-text)" strokeWidth={2.3} />}
              tone="yellow"
              label="Prestations totales"
              value={formatNumber(stats.totalPrestations)}
              detail="Toutes périodes"
            />
            <StatCard
              icon={<CalendarDays size={26} color="var(--accent-text)" strokeWidth={2.3} />}
              tone="yellow"
              label="Prestations mensuelles"
              value={formatNumber(stats.monthlyPrestations)}
              detail="Ce mois"
            />
            <StatCard
              icon={<Coins size={26} color="var(--trust-deep)" strokeWidth={2.3} />}
              tone="green"
              label="Profits totaux"
              value={formatNumber(stats.totalProfit)}
              suffix="MAD"
              detail="Toutes périodes"
            />
            <StatCard
              icon={<Wallet size={26} color="var(--trust-deep)" strokeWidth={2.3} />}
              tone="green"
              label="Profits mensuels"
              value={formatNumber(stats.monthlyProfit)}
              suffix="MAD"
              detail="Ce mois"
            />
          </div>
        </section>

        <section>
          <h2 className={styles.sectionTitle}>Performance</h2>
          <div className={styles.performanceCard}>
            <PerformanceRow
              to="/pro/reviews"
              tone="yellow"
              icon={<Star size={28} color="#FFB21A" fill="#FFB21A" strokeWidth={0} />}
              label="Note moyenne"
              value={`${stats.rating ? stats.rating.toFixed(1) : '0.0'} / 5`}
            />
            <PerformanceRow
              to="/pro/reviews"
              tone="yellow"
              icon={
                <MessageCircle
                  size={26}
                  color="var(--accent-text)"
                  fill="var(--accent-text)"
                  strokeWidth={0}
                />
              }
              label="Avis reçus"
              value={String(stats.reviewsCount)}
            />
            <PerformanceRow
              to="/pro/requests"
              tone="green"
              icon={<CircleCheck size={28} color="var(--trust-deep)" strokeWidth={2.6} />}
              label="Taux d'acceptation"
              value={`${stats.acceptanceRate}%`}
            />
          </div>
        </section>
      </div>
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  suffix,
  detail,
  tone,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  suffix?: string;
  detail: string;
  tone: 'yellow' | 'green';
}) {
  return (
    <div className={styles.statCard}>
      <span className={`${styles.iconCircle} ${styles[tone]}`}>{icon}</span>
      <div className={styles.statBody}>
        <p className={styles.statLabel}>{label}</p>
        <p className={`${styles.statValue} num`}>
          {value}
          {suffix ? <span className={styles.statSuffix}> {suffix}</span> : null}
        </p>
        <p className={styles.statDetail}>{detail}</p>
      </div>
    </div>
  );
}

function PerformanceRow({
  to,
  icon,
  label,
  value,
  tone,
}: {
  to: string;
  icon: ReactNode;
  label: string;
  value: string;
  tone: 'yellow' | 'green';
}) {
  return (
    <Link className={styles.performanceRow} to={to}>
      <span className={`${styles.iconCircle} ${styles[tone]}`}>{icon}</span>
      <div className={styles.performanceBody}>
        <p className={styles.performanceLabel}>{label}</p>
        <p className={`${styles.performanceValue} num`}>{value}</p>
      </div>
      <ChevronRight size={22} className={styles.rowChevron} />
    </Link>
  );
}
